import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { DECISION_LABEL, KIND_LABEL, STATUS_META, fmtDateTime } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { EmptyState, PageHeader, ScoreRing, Spinner, StatusBadge } from "@/components/shared";
import { cn } from "@/lib/utils";

const STATUSES = ["NEEDS_REVIEW", "UNMATCHED", "POSSIBLE_MATCH", "MATCHED", "CANCELLED"];

export default function Reconciliation() {
  const [params] = useSearchParams();
  const [statuses, setStatuses] = useState<string[]>(params.get("pending") ? ["NEEDS_REVIEW", "UNMATCHED", "POSSIBLE_MATCH"] : []);
  const [pending, setPending] = useState(!!params.get("pending"));
  const [q, setQ] = useState("");
  const [range, setRange] = useState<[number, number]>([0, 100]);
  const { data, isLoading } = useQuery({
    queryKey: ["reconciliation", statuses, pending, q],
    queryFn: async () => (await api.get("/reconciliation", { params: { status: statuses.join(",") || undefined, pending: pending || undefined, q: q || undefined, hours: 48 } })).data,
    refetchInterval: 30000,
  });
  const rows = (data || []).filter((r: any) => r.score == null || (r.score >= range[0] && r.score <= range[1]));
  const toggle = (s: string) => setStatuses((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <div data-testid="reconciliation-page">
      <PageHeader title="Reconciliation inbox" subtitle="Declared Uber trips vs physical journeys · neutral review workflow" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-4">
          <div className="tg-card p-4 space-y-3" data-testid="reconciliation-filters">
            <div className="tg-label">Status</div>
            {STATUSES.map((s) => (
              <button key={s} data-testid={`filter-status-${s.toLowerCase()}`} onClick={() => toggle(s)} className={cn("w-full flex items-center justify-between rounded-md px-3 py-2 text-sm border transition-colors", statuses.includes(s) ? "border-blue-500/50 bg-blue-500/10" : "border-transparent hover:bg-[#1f2a3e]")}>
                <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s].hex }} />{STATUS_META[s].label}</span>
              </button>
            ))}
            <label className="flex items-center gap-2 text-sm text-slate-300 pt-2 border-t border-[#26334d]"><input data-testid="filter-pending-checkbox" type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} className="accent-blue-500" /> Only without decision</label>
            <div className="tg-label pt-2">Score range <span className="text-slate-200">{range[0]}–{range[1]}</span></div>
            <div className="flex gap-2">
              <input data-testid="filter-score-min" type="range" min={0} max={100} value={range[0]} onChange={(e) => setRange([Math.min(+e.target.value, range[1]), range[1]])} className="w-full accent-blue-500" />
              <input data-testid="filter-score-max" type="range" min={0} max={100} value={range[1]} onChange={(e) => setRange([range[0], Math.max(+e.target.value, range[0])])} className="w-full accent-blue-500" />
            </div>
            <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-500" /><Input data-testid="filter-search-input" placeholder="Plate, driver, trip ID" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 bg-[#0f141f] border-[#26334d] text-sm" /></div>
          </div>
        </aside>
        <section className="lg:col-span-9">
          {isLoading ? <Spinner /> : rows.length === 0 ? <EmptyState text="Nothing matches these filters" testId="reconciliation-empty" /> : (
            <div className="space-y-2" data-testid="reconciliation-list">
              <div className="tg-label px-1">{rows.length} records</div>
              {rows.map((r: any) => (
                <Link key={r.id} to={`/reconciliation/${r.id}`} data-testid={`rec-card-${r.id}`} className="tg-card tg-card-hover p-3 flex items-center gap-4 block">
                  <ScoreRing score={r.score} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-slate-100">{r.plate || "—"}</span>
                      <span className="text-xs text-slate-400">{r.driverName || "driver unknown"}</span>
                      <span className="text-[10px] font-mono uppercase text-slate-500 border border-[#26334d] rounded px-1.5">{KIND_LABEL[r.kind]}</span>
                    </div>
                    <div className="text-xs text-slate-400 truncate mt-0.5">{r.reasons?.[0]}</div>
                    <div className="font-mono text-[11px] text-slate-500 mt-0.5">{r.externalTripId || "no declared trip"} · {fmtDateTime(r.eventTime)}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={r.status} />
                    {r.decision && <span data-testid={`rec-decision-${r.id}`} className="font-mono text-[10px] text-blue-300">✓ {DECISION_LABEL[r.decision]}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
